import React, { useState, useRef, useEffect } from "react";
import { useGoogleMapsLoader } from "../../../GoogleMapsProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  handleShowProfile,
  handleUpdateAddress,
  handleUpdateProfile,
} from "../../../frontend/auth/Auth";
import { updateUser } from "../../../store/slice/user";
import { toast } from "react-toastify";
import isValidURL from "../../../ValidateUrl";
import MapWithConditionalRendering from "../../../frontend/components/Map/MapWithConditionRendering";

const DEFAULT_LOCATION = { lat: 24.8607, lng: 67.0011 };

export const ProfileForm = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState("");

  // Coordinates — set only by: autocomplete selection, marker drag, GPS
  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });

  // Address — set only by: reverseGeocode callback from map, or initial profile load
  // It is NEVER set by the landmark input inside the map component
  const [address, setAddress] = useState("");

  // Gate for submit button: true only after user actively confirms via map/landmark/GPS
  const [isLocationConfirmed, setIsLocationConfirmed] = useState(false);

  const [isPending, setIsPending]     = useState(false);
  const [shouldReload, setShouldReload] = useState(false);

  const [profileData, setProfileData] = useState({
    id: "", first_name: "", last_name: "",
    email: "", phone: "", bio: "",
    profile_pic: "", cover_pic: "",
  });

  const [imageToUpload, setImageToUpload] = useState({ profile: "", cover: "" });

  const { authToken } = useSelector((state) => state.user);

  const { isLoaded } = useGoogleMapsLoader();

  // ── Fetch Profile ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const response = await handleShowProfile(authToken);
      if (response) {
        localStorage.setItem("user", JSON.stringify(response));
        dispatch(updateUser(response));
        setProfileData(response);

        if (response.chef_addresses?.length > 0) {
          const last = response.chef_addresses[response.chef_addresses.length - 1];
          const lat  = parseFloat(last.latitude);
          const lng  = parseFloat(last.longitude);

          setCoordinates({ lat, lng });
          setAddress(last.address);

          // Pre-existing saved address counts as confirmed
          if (lat && lng && last.address) setIsLocationConfirmed(true);
        }
      }
    })();
  }, [authToken, dispatch, shouldReload]);

  const hasCoordinates      = coordinates.lat !== "" && coordinates.lng !== "";
  const effectiveCoordinates = hasCoordinates ? coordinates : DEFAULT_LOCATION;

  // Submit is enabled only when:
  //  • no validation errors
  //  • user actively confirmed location (dragged pin / picked landmark / used GPS)
  //  • we have real lat/lng
  //  • address string is non-empty
  const isSubmitDisabled =
    isPending ||
    !!error ||
    !isLocationConfirmed ||
    !hasCoordinates ||
    !address.trim();

  // ── Callbacks from map ────────────────────────────────────────────────────

  // Called by map whenever a reverse geocode completes (drag, GPS, autocomplete)
  // This is the ONLY place that sets the address field
  const handleReverseGeocode = (formattedAddress) => {
    setAddress(formattedAddress);
  };

  // Called when autocomplete or GPS picks a location
  const handlePlaceSelect = ({ lat, lng }) => {
    setCoordinates({ lat, lng });
  };

  // Called when marker is dragged — update coordinates, address set via handleReverseGeocode
  const onDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setCoordinates({ lat, lng });
  };

  // ── Profile field change ──────────────────────────────────────────────────
  const handleProfileDataChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleOnSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    const cleanPhone = profileData.phone.replace(/\D/g, "");
    if (cleanPhone.length !== 11) {
      toast.error("Phone number must be exactly 11 digits");
      return;
    }

    setIsPending(true);
    try {
      const formData = new FormData();
      formData.append("id",         profileData.id);
      formData.append("first_name", profileData.first_name);
      formData.append("last_name",  profileData.last_name);
      formData.append("email",      profileData.email);
      formData.append("phone",      profileData.phone);
      formData.append("bio",        profileData.bio);
      if (imageToUpload.profile) formData.append("profile_pic", imageToUpload.profile);
      if (imageToUpload.cover)   formData.append("cover_pic",   imageToUpload.cover);

      const addressFormData = new FormData();
      addressFormData.append("name",      profileData.first_name);
      addressFormData.append("phone",     profileData.phone);
      addressFormData.append("address",   address);
      addressFormData.append("longitude", coordinates.lng);
      addressFormData.append("latitude",  coordinates.lat);

      await handleUpdateProfile(authToken, formData);
      await handleUpdateAddress(authToken, addressFormData);

      toast.success("Profile Updated");
      setShouldReload((prev) => !prev);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsPending(false);
    }
  };

  // ── Image handlers ────────────────────────────────────────────────────────
  const fileInputRef      = useRef(null);
  const coverFileInputRef = useRef(null);

  return (
    <>
      <div className="p-5 rounded-xl border border-borderClr mb-6">
        <h2 className="text-xl font-semibold border-b pb-2 mb-4">Profile Info</h2>
        <form onSubmit={handleOnSubmit}>

          {/* ── Cover & Profile Images ── */}
          <div className="flex gap-4 flex-wrap flex-col">
            <div
              className="w-full h-[140px] sm:h-[200px] border border-borderClr rounded-lg overflow-hidden relative cursor-pointer"
              onClick={() => coverFileInputRef.current.click()}
            >
              {profileData.cover_pic && isValidURL(profileData.cover_pic) ? (
                <img src={profileData.cover_pic} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <p className="text-center flex items-center justify-center text-sm text-[#777] h-full">
                  Click to add Cover
                </p>
              )}
            </div>
            <input type="file" accept="image/*" ref={coverFileInputRef} hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setImageToUpload((prev) => ({ ...prev, cover: file }));
                const reader = new FileReader();
                reader.onload = () => setProfileData((prev) => ({ ...prev, cover_pic: reader.result }));
                reader.readAsDataURL(file);
              }}
            />

            <div
              className="w-[170px] h-[190px] border border-borderClr rounded-lg overflow-hidden relative cursor-pointer"
              onClick={() => fileInputRef.current.click()}
            >
              {profileData.profile_pic && isValidURL(profileData.profile_pic) ? (
                <img src={profileData.profile_pic} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <p className="text-center flex items-center justify-center text-sm text-[#777] h-full">
                  Click to add Profile
                </p>
              )}
            </div>
            <input type="file" accept="image/*" ref={fileInputRef} hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setImageToUpload((prev) => ({ ...prev, profile: file }));
                const reader = new FileReader();
                reader.onload = () => setProfileData((prev) => ({ ...prev, profile_pic: reader.result }));
                reader.readAsDataURL(file);
              }}
            />
          </div>

          <div className="grid grid-cols-12 md:gap-x-8 gap-x-0 gap-y-4 mt-8">

            {/* ── First / Last Name ── */}
            <div className="md:col-span-6 col-span-12">
              <h4 className="text-base font-semibold mb-1 uppercase">
                First Name <span className="text-primary">*</span>
              </h4>
              <input type="text" name="first_name" required
                onChange={handleProfileDataChange} value={profileData.first_name} />
            </div>

            <div className="md:col-span-6 col-span-12">
              <h4 className="text-base font-semibold mb-1 uppercase">Last Name</h4>
              <input type="text" name="last_name" required
                onChange={handleProfileDataChange} value={profileData.last_name} />
            </div>

            {/* ── Email ── */}
            <div className="md:col-span-6 col-span-12">
              <h4 className="text-base font-semibold mb-1 uppercase">
                Email <span className="text-primary">*</span>
              </h4>
              <input type="email" value={profileData.email} readOnly
                className="read-only:bg-gray-50 !border-headGray" />
            </div>

            {/* ── Phone ── */}
            <div className="md:col-span-6 col-span-12">
              <h4 className="text-base font-semibold mb-1 uppercase">
                Mobile Number <span className="text-primary">*</span>
              </h4>
              <input
                type="text"
                name="phone"
                value={profileData.phone}
                onChange={handleProfileDataChange}
                placeholder="032xxxxxxxxx"
                required
                maxLength={11}
                pattern="^03[0-9]{9}$"
              />
            </div>

            {/* ── ADDRESS FIELD (standalone, above the map) ── */}
            <div className="col-span-12">
              <h4 className="text-base font-semibold mb-1 uppercase">
                Address <span className="text-primary">*</span>
              </h4>

              <div className="relative">
                <input
                  type="text"
                  value={address}
                  readOnly
                  placeholder="Auto-filled when you select a location below"
                  className={`w-full border rounded-md px-3 py-2 pr-9 text-sm bg-gray-50 cursor-default select-none
                    ${isLocationConfirmed && address
                      ? "border-green-400 bg-green-50 text-green-800"
                      : "border-gray-300 text-gray-500"
                    }`}
                />
                {isLocationConfirmed && address && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                )}
              </div>

              {/* Prompt user to interact with map if not yet confirmed */}
              {!isLocationConfirmed && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10
                             10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  Use the landmark search or drag the map pin to confirm your location.
                </p>
              )}
            </div>

            {/* ── MAP + LANDMARK SEARCH (all location interaction lives here) ── */}
            <div className="col-span-12">
              <h4 className="text-base font-semibold mb-2 uppercase">
                Pin Your Location <span className="text-primary">*</span>
              </h4>

              <div className="border rounded-lg overflow-hidden p-3 bg-white">
                {isLoaded ? (
                  <MapWithConditionalRendering
                    coordinates={effectiveCoordinates}
                    onDragEnd={onDragEnd}
                    showMap={true}
                    onPlaceSelect={handlePlaceSelect}
                    onReverseGeocode={handleReverseGeocode}
                    onLocationConfirmed={setIsLocationConfirmed}
                  />
                ) : (
                  <div className="h-[250px] bg-gray-100 flex items-center justify-center text-sm text-gray-500">
                    Initializing Map…
                  </div>
                )}
              </div>
            </div>

            {/* ── Bio ── */}
            <div className="col-span-12">
              <h4 className="text-base font-semibold mb-1 uppercase">Bio</h4>
              <textarea
                name="bio"
                className="h-[70px]"
                onChange={handleProfileDataChange}
                value={profileData.bio}
                placeholder="Enter Bio"
              />
            </div>

            {/* ── Submit ── */}
            <div className="md:col-span-6 mt-3 col-span-12 flex flex-col items-start gap-1">
              {isSubmitDisabled && !isPending && (
                <p className="text-xs text-gray-400">
                  {!isLocationConfirmed
                    ? "📍 Confirm your location to enable submit"
                    : !address.trim()
                    ? "Address is required"
                    : "Fix the errors above to continue"}
                </p>
              )}
              <button
                disabled={isSubmitDisabled}
                className="bg-primary text-white p-[8px_24px] rounded text-base font-semibold uppercase
                           disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                type="submit"
              >
                {isPending ? "Updating…" : "Submit"}
              </button>
            </div>

          </div>
        </form>
      </div>
    </>
  );
};

export default ProfileForm;